import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  useNotifications,
  useUnreadCount,
  useMarkRead,
  useMarkAllRead,
  useDismissNotification,
} from '../../hooks/useNotifications.js';
import { useFeatureFlag } from '../../lib/posthog.js';

// ── Relative time ──────────────────────────────────────────────────────────────

export function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

// ── NotificationBell ───────────────────────────────────────────────────────────

export default function NotificationBell() {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const notificationsEnabled = useFeatureFlag('notifications');

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: unreadData } = useUnreadCount();
  const { data: items = [], isLoading } = useNotifications();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();
  const dismiss = useDismissNotification();

  const unreadCount = unreadData?.count ?? 0;
  const badgeLabel = unreadCount > 99 ? '99+' : unreadCount > 0 ? String(unreadCount) : null;

  // Close panel on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!notificationsEnabled) return null;

  const handleClick = (notif: typeof items[number]) => {
    if (!notif.isRead) void markRead.mutate(notif.id);
    if (notif.url) {
      navigate(notif.url);
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        onClick={() => setIsOpen((o) => !o)}
        aria-label={t('title')}
        style={{
          position: 'relative',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 'var(--space-2)',
          borderRadius: 'var(--radius-md)',
          color: isOpen ? 'var(--color-brand)' : 'var(--color-text-secondary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-bg-subtle)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
      >
        <Bell size={18} />
        {badgeLabel && (
          <span
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              minWidth: 16,
              height: 16,
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-danger)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 'var(--font-bold)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 3px',
              lineHeight: 1,
            }}
          >
            {badgeLabel}
          </span>
        )}
      </button>

      {/* Panel */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + var(--space-2))',
            right: 0,
            width: 380,
            maxWidth: 'calc(100vw - 32px)',
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 200,
            maxHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--color-border)' }}>
            <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text)' }}>
              {t('title')}
            </p>
            {unreadCount > 0 && (
              <button
                onClick={() => void markAllRead.mutate()}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--text-xs)', color: 'var(--color-brand)', padding: 0 }}
              >
                {t('mark_all_read')}
              </button>
            )}
          </div>

          {/* Items */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {isLoading && (
              <div style={{ padding: 'var(--space-4)' }}>
                {[1, 2, 3].map((i) => (
                  <div key={i} className="skeleton-line" style={{ height: 56, marginBottom: 8, borderRadius: 'var(--radius-md)' }} />
                ))}
              </div>
            )}

            {!isLoading && items.length === 0 && (
              <p style={{ padding: 'var(--space-8)', textAlign: 'center', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                {t('empty')}
              </p>
            )}

            {!isLoading && items.map((notif, idx) => (
              <div
                key={notif.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-3) var(--space-4)',
                  borderBottom: idx < items.length - 1 ? '1px solid var(--color-border)' : 'none',
                  background: notif.isRead ? 'none' : 'var(--color-brand-pale)',
                  cursor: notif.url ? 'pointer' : 'default',
                }}
                onClick={() => handleClick(notif)}
              >
                {/* Unread dot */}
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: notif.isRead ? 'transparent' : 'var(--color-brand)', flexShrink: 0, marginTop: 6 }} />

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 'var(--text-sm)', fontWeight: notif.isRead ? 'var(--font-regular)' : 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 2 }}>
                    {notif.title}
                  </p>
                  {notif.body && (
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {notif.body}
                    </p>
                  )}
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    {formatRelativeTime(notif.createdAt)}
                  </p>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 'var(--space-1)', flexShrink: 0 }}>
                  {!notif.isRead && (
                    <button
                      onClick={(e) => { e.stopPropagation(); void markRead.mutate(notif.id); }}
                      aria-label={t('mark_read')}
                      title={t('mark_read')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: 4, borderRadius: 'var(--radius-sm)' }}
                    >
                      <Check size={13} />
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); void dismiss.mutate(notif.id); }}
                    aria-label={t('dismiss')}
                    title={t('dismiss')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: 4, borderRadius: 'var(--radius-sm)' }}
                  >
                    <X size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
